import React, {
    FormEvent,
    useState,
    useCallback,
    ReactElement,
    forwardRef,
} from "react";
import { FaExclamationCircle } from "react-icons/fa";
// Import your new refactored components
import Dimmer, { DimmerProps } from "../dimmer/dimmer";
import Form, { FormProps } from "../form/form"; // Use your new Form
import { Loader } from "../loader";      // Use your new Loader

// 1. Import the new CSS file
import "./validationForm.css";

// --- VALIDATION ERROR CLASS (Copied directly from original) ---

/**
 * Field name -> errors mapping
 */
export interface FormValidationErrorMap {
    [key: string]: string;
}

export interface FormValidatorErrorMap {
    [key: string]: string | false | undefined;
}

/**
 * Represent server validation errors
 */
export class FormValidationError extends Error {
    /**
     * Convert Validation error to FormServerValidationError
     * @param error
     * @param keyReplacer
     */
    public static fromErrorDictionary(
        error: { [key: string]: string },
        keyReplacer?: { [key:string]: string }
    ): FormValidationError {
        const newErrors: FormValidationErrorMap = {};
        for (const key of Object.keys(error)) {
            newErrors[
                keyReplacer && keyReplacer[key] ? keyReplacer[key] : key
            ] = error[key];
        }
        return new FormValidationError(newErrors);
    }
    /**
     * Errors
     */
    public readonly errors: FormValidationErrorMap;

    /**
     * @constructor
     * @param errors
     */
    public constructor(errors: FormValidationErrorMap) {
        super("Validation error");
        Object.setPrototypeOf(this, FormValidationError.prototype);
        this.errors = errors;
    }
}

// --- COMPONENT PROPS ---

export interface ValidationFormProps extends FormProps {
    /**
     * Form dimmer color
     */
    uiDimmerColor?: DimmerProps["uiColor"];
    /**
     * Dimmer blurring
     * @default true
     */
    uiDimmerBlurring?: boolean;
    /**
     * Dim form and render loader when submitting
     */
    uiUseDimmer?: boolean;
    /**
     * Render error icon for error message
     * When true, renders default error icon
     * When ReactElement given renders it instead
     */
    uiSubmitFailedIcon?: true | ReactElement<any>;
    /**
     * General submit failed message
     */
    uiSubmitFailedMessage?: string;
    /**
     * If true when uses submit failed message from Error object
     */
    uiUseSubmitFailedMessageFromError?: boolean;
    /**
     * Error reporting mode:
     * * firstInvalid - will report error only for first invalid field. Preferred mode if you're displaying errors in popup
     * * allInvalid - will report error for all invalid fields
     * @default "allInvalid"
     */
    uiReportValidityMode?: "firstInvalid" | "allInvalid";
    /**
     * Custom validator run before submit. Note: only run if standard elements are already valid
     * @param e Form element
     */
    uiFormValidator?(e: HTMLFormElement): FormValidatorErrorMap;
    /**
     * Submit callback
     * @param e Form event
     */
    uiOnSubmit(e: FormEvent<HTMLFormElement>): Promise<void>;
    /**
     * Called for form element which has failed validation with "firstInvalid" mode
     * @param e HTMLelement with failed validation
     */
    uiOnInvalidElem?(e: HTMLElement): void;
    /**
     * Called when failed form validation (or any form element validation)
     * @param e HTML form element
     */
    uiOnInvalidForm?(e: HTMLFormElement): void;
}

// --- NEW FUNCTIONAL COMPONENT ---

const ValidationForm = forwardRef<HTMLFormElement, ValidationFormProps>(
    (props, ref) => {
        const {
            // Destructure all props, setting defaults
            children,
            uiDimmerColor = "transparent",
            uiDimmerBlurring = false,
            uiSubmitFailedMessage = "There was an error. If the problem persists, please contact support.",
            uiUseDimmer = true,
            uiReportValidityMode = "allInvalid",
            uiUseSubmitFailedMessageFromError,
            uiOnSubmit,
            uiSubmitFailedIcon,
            uiFormValidator,
            uiOnInvalidElem,
            uiOnInvalidForm,
            className,
            ...other
        } = props;

        // 2. State managed with Hooks
        const [submitting, setSubmitting] = useState(false);
        const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

        // 3. handleSubmit logic moved to useCallback
        const handleSubmit = useCallback(
            async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
                e.stopPropagation();
                e.preventDefault();
                const form = e.currentTarget;

                // --- HTML5 Validation ---
                if (uiReportValidityMode === "allInvalid") {
                    if (!form.reportValidity()) {
                        if (uiOnInvalidForm) uiOnInvalidForm(form);
                        return;
                    }
                } else {
                    let hasInvalid = false;
                    for (let i = 0; i < form.elements.length; i++) {
                        const elem = form.elements.item(i);
                        if (elem && "checkValidity" && "reportValidity" in elem) {
                            const elemValid = (
                                elem as HTMLInputElement
                            ).checkValidity();
                            if (!elemValid) {
                                hasInvalid = true;
                                (elem as HTMLInputElement).reportValidity();
                                if (uiOnInvalidElem) uiOnInvalidElem(elem as any);
                                break;
                            }
                        }
                    }
                    if (hasInvalid) {
                        if (uiOnInvalidForm) uiOnInvalidForm(form);
                        return;
                    }
                }

                // --- Custom uiFormValidator ---
                if (uiFormValidator) {
                    const validity = uiFormValidator(form);
                    if (validity && Object.entries(validity).find(([, val]) => !!val)) {
                        for (const [field, val] of Object.entries(validity)) {
                            if (!val) continue;
                            const elem = form.elements.namedItem(field);
                            if (elem && "setCustomValidity" in elem) {
                                (elem as HTMLInputElement).setCustomValidity(val);
                                if (uiReportValidityMode === "firstInvalid") {
                                    (elem as HTMLInputElement).reportValidity();
                                    if (uiOnInvalidElem) uiOnInvalidElem(elem as any);
                                    break;
                                }
                            }
                        }
                        if (uiReportValidityMode === "allInvalid") {
                            form.reportValidity();
                        }
                        if (uiOnInvalidForm) uiOnInvalidForm(form);
                        return;
                    }
                }

                // --- Async Submission ---
                try {
                    if (uiUseDimmer) {
                        setSubmitting(true);
                    }
                    await uiOnSubmit(e);
                    // Clear errors on successful submit
                    setErrorMsg(undefined); 
                } catch (error) {
                    const err: any = error
                    if (err instanceof FormValidationError) {
                        if (!Object.keys(err.errors).length) {
                            setErrorMsg(err.message);
                            return;
                        }
                        for (const key of Object.keys(err.errors)) {
                            const elem = form.elements.namedItem(key);
                            if (
                                elem &&
                                "setCustomValidity" in elem &&
                                err.errors[key]
                            ) {
                                (elem as HTMLInputElement).setCustomValidity(err.errors[key]);
                                if (uiOnInvalidElem) uiOnInvalidElem(elem as any);
                            }
                        }
                        form.reportValidity();
                        if (uiOnInvalidForm) uiOnInvalidForm(form);
                    } else {
                        const message = err.message.split(":")[1] || err.message;
                        setErrorMsg(message.trim());
                    }
                } finally {
                    if (uiUseDimmer) {
                        setSubmitting(false);
                    }
                }
            },
            [
                uiOnSubmit,
                uiUseDimmer,
                uiFormValidator,
                uiReportValidityMode,
                uiOnInvalidElem,
                uiOnInvalidForm,
            ]
        );

        // 4. Render logic
        const errorIcon =
            typeof uiSubmitFailedIcon === "boolean" ? (
                <FaExclamationCircle />
            ) : (
                uiSubmitFailedIcon || null
            );
        
        const classNames = ["validation-form", className].filter(Boolean).join(" ");

        return (
            <Form
                {...other}
                className={classNames}
                noValidate // We use our own validation
                onSubmit={handleSubmit}
                ref={ref}
            >
                {/* Use your new refactored Dimmer and Loader */}
                <Dimmer
                    uiBlurring={uiDimmerBlurring}
                    uiColor={uiDimmerColor}
                    uiActive={submitting}
                >
                    <Loader />
                </Dimmer>

                {children}

                {/* Error message rendering */}
                {uiSubmitFailedMessage !== 'There was an error. If the problem persists, please contact support.' && errorMsg && !submitting && (
                    <div className="error-msg">
                        {errorIcon}
                        <span>
                            {uiUseSubmitFailedMessageFromError
                                ? uiSubmitFailedMessage
                                : errorMsg ||
                                "There was an error submitting this form. If the problem persists, please contact support."}
                        </span>
                    </div>
                )}
            </Form>
        );
    }
);

ValidationForm.displayName = "ValidationForm";

export default ValidationForm;
